/**
 * Optimistic jobs-list patches from overlays that do not own `setJobs`
 * (the lifecycle PeriodSummary dock is root-mounted, outside AdminHome).
 *
 * Same reason handleDeleteJob filters locally instead of waiting on
 * postgres_changes: the user closes the modal and expects the visit gone
 * from the board the same tick, not after WAL. Realtime DELETE is the
 * backup; this is the write-is-truth drop.
 *
 * Payloads:
 *   { type: 'remove', jobIds: string[] }  — whole-job delete succeeded
 *   { type: 'patch',  jobId, total }      — JobDetail save succeeded
 *   { type: 'upsert', job }               — jobs INSERT/UPDATE. Opening/Closing
 *                                           pills only; extras (`visit_reason`)
 *                                           stay on the jobs[] extra rows.
 *
 * Never fire `remove` for a line-only strip — the visit stays.
 *
 * Opening/Closing green pills read pool-period bundles, not the dashboard
 * `jobs` array. `registerJobsListMutationStoreApplier` lets the bundle
 * cache drop/add the same ids so a closed sheet or another role's log
 * cannot leave the board stale. `upsert` is bundle-only — do not apply it
 * to a worker `jobs` array (that list is worker_id-scoped).
 */
let listeners = new Set();
let storeAppliers = [];

export function registerJobsListMutationStoreApplier(fn) {
  if (typeof fn !== 'function') return () => {};
  storeAppliers.push(fn);
  return () => {
    storeAppliers = storeAppliers.filter((f) => f !== fn);
  };
}

function notify(list, detail) {
  [...list].forEach((fn) => {
    try {
      fn(detail);
    } catch (err) {
      console.warn('[jobsListMutationSignal] listener threw', err);
    }
  });
}

export function bumpJobsListMutation(detail) {
  notify(storeAppliers, detail);
  notify(listeners, detail);
}

export function subscribeJobsListMutation(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * postgres_changes on `jobs` → the same signal the duplicate-delete dock
 * uses, so every mounted board patches Opening/Closing pills without waiting
 * on dataRefreshSeq / PTR.
 */
export function bumpPoolPeriodFromJobRealtime({ eventType, newRow, oldRow }) {
  if (eventType === 'DELETE' && oldRow?.id) {
    bumpJobsListMutation({ type: 'remove', jobIds: [oldRow.id] });
    return;
  }
  if ((eventType === 'INSERT' || eventType === 'UPDATE') && newRow?.id) {
    bumpJobsListMutation({ type: 'upsert', job: newRow });
  }
}

/** Pure apply for a dashboard `jobs` array. Unknown payloads are a no-op. */
export function applyJobsListMutation(prevJobs, detail) {
  if (!Array.isArray(prevJobs) || !detail) return prevJobs;
  if (detail.type === 'remove') {
    const ids = new Set((detail.jobIds || []).map(String));
    if (ids.size === 0) return prevJobs;
    return prevJobs.filter((j) => !ids.has(String(j.id)));
  }
  if (detail.type === 'patch' && detail.jobId != null && detail.total != null) {
    return prevJobs.map((j) => (
      String(j.id) === String(detail.jobId) ? { ...j, total: detail.total } : j
    ));
  }
  return prevJobs;
}

function bundleTotal(jobs) {
  return jobs.reduce((sum, j) => sum + (Number(j.total) || 0), 0);
}

function toBundleVisit(job) {
  return {
    id: job.id,
    date: job.date,
    total: Number(job.total) || 0,
    unpriced_line_count: Number(job.unpriced_line_count) || 0,
    lines: Array.isArray(job.lines) ? job.lines : [],
  };
}

function jobEligibleForPeriodBundle(job, bundle, context, customerId) {
  if (!job?.id || job.customer_id == null) return false;
  if (customerId != null && String(job.customer_id) !== String(customerId)) return false;
  const ctx = job.job_context;
  if (ctx !== 'opening' && ctx !== 'closing') return false;
  if (context && String(ctx) !== String(context)) return false;
  // Unscheduled extras render from the jobs[] extra rows, not period pills.
  if (job.visit_reason) return false;
  const billed = new Set((bundle.billedJobIds || []).map(String));
  if (billed.has(String(job.id))) return false;
  if (bundle.windowStart && job.date && job.date < bundle.windowStart) return false;
  return true;
}

function sortBundleVisits(jobs) {
  return [...jobs].sort((a, b) => {
    const byDate = String(a.date || '').localeCompare(String(b.date || ''));
    if (byDate !== 0) return byDate;
    return String(a.id).localeCompare(String(b.id));
  });
}

/** Pure apply for one pool-period bundle `{ jobs, jobCount, total, ... }`. */
export function applyJobsListMutationToBundle(bundle, detail, scope = {}) {
  if (!bundle || !detail) return bundle;
  const jobs = Array.isArray(bundle.jobs) ? bundle.jobs : [];
  if (detail.type === 'remove') {
    const ids = new Set((detail.jobIds || []).map(String));
    if (ids.size === 0) return bundle;
    const nextJobs = jobs.filter((j) => !ids.has(String(j.id)));
    if (nextJobs.length === jobs.length) return bundle;
    return {
      ...bundle,
      jobs: nextJobs,
      jobCount: nextJobs.length,
      total: bundleTotal(nextJobs),
    };
  }
  if (detail.type === 'patch' && detail.jobId != null && detail.total != null) {
    let found = false;
    const nextJobs = jobs.map((j) => {
      if (String(j.id) !== String(detail.jobId)) return j;
      found = true;
      return { ...j, total: detail.total };
    });
    if (!found) return bundle;
    return { ...bundle, jobs: nextJobs, total: bundleTotal(nextJobs) };
  }
  if (detail.type === 'upsert' && detail.job) {
    const job = detail.job;
    if (!jobEligibleForPeriodBundle(job, bundle, scope.context, scope.customerId)) return bundle;
    const visit = toBundleVisit(job);
    const idx = jobs.findIndex((j) => String(j.id) === String(visit.id));
    let nextJobs;
    if (idx === -1) {
      nextJobs = sortBundleVisits([...jobs, visit]);
    } else {
      const prev = jobs[idx];
      if (prev.total === visit.total && prev.date === visit.date) return bundle;
      nextJobs = jobs.slice();
      nextJobs[idx] = { ...prev, ...visit, lines: prev.lines?.length ? prev.lines : visit.lines };
    }
    return { ...bundle, jobs: nextJobs, jobCount: nextJobs.length, total: bundleTotal(nextJobs) };
  }
  return bundle;
}

/** Pure apply across a customer-id → bundle Map. Same identity if nothing changed. */
export function applyJobsListMutationToBundleMap(bundles, detail, context) {
  if (!bundles || typeof bundles.forEach !== 'function' || !detail) return bundles;
  if (detail.type === 'upsert' && detail.job?.customer_id != null) {
    if (context && detail.job.job_context && String(detail.job.job_context) !== String(context)) {
      return bundles;
    }
    const cid = String(detail.job.customer_id);
    const bundle = bundles.get(cid);
    if (!bundle) return bundles;
    const applied = applyJobsListMutationToBundle(bundle, detail, { context, customerId: cid });
    if (applied === bundle) return bundles;
    const next = new Map(bundles);
    next.set(cid, applied);
    return next;
  }
  let changed = false;
  const next = new Map();
  bundles.forEach((bundle, key) => {
    const applied = applyJobsListMutationToBundle(bundle, detail, { context, customerId: key });
    if (applied !== bundle) changed = true;
    next.set(key, applied);
  });
  return changed ? next : bundles;
}

/** Test-only — module state survives across `it()` blocks. */
export function __resetJobsListMutationListenersForTests() {
  listeners = new Set();
}
